import { n as __toESM, t as __commonJSMin } from "../chunks/rolldown-runtime-CO0Nlsmt.js";
import { $ as Skeleton, At as IconButton, L as Drawer, Lt as Accordion, Nt as Box, St as Flex, rt as Tooltip, st as Text } from "../chunks/components-9RkKXUJL.js";
import { Tt as GhostIcon, Ut as CircleXIcon, Zt as ChevronRightIcon, at as MenuIcon, fn as ArrowRightToLineIcon, pn as ArrowLeftToLineIcon } from "../chunks/icons-CPQCRIUN.js";
import { Fragment, createContext, useCallback, useContext, useEffect, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { FloatingNode, FloatingPortal, FloatingTree, safePolygon, useFloating, useFloatingNodeId, useHover, useInteractions } from "@floating-ui/react";
// src/widgets/authenticated-layout/constants.ts
const HEADER_AND_SIDEBAR_WIDTH = "60px";
const HEADER_HEIGHT = HEADER_AND_SIDEBAR_WIDTH;
const SIDEBAR_COLLAPSED_WIDTH = HEADER_AND_SIDEBAR_WIDTH;
const SIDEBAR_EXPANDED_WIDTH = "230px";
const SIDEBAR_BACKGROUND_COLOR = "shell";
const TITLE_COLOR = "shell";
// src/widgets/authenticated-layout/AppGrid.tsx
function AppGrid({ isOpen, logo, topbar, nav, mobileHeader, children }) {
	return /* @__PURE__ */ jsxs(Flex, {
		flexDirection: "column",
		height: "100vh",
		width: "100vw",
		overflow: "hidden",
		padding: {
			base: "0",
			md: "2"
		},
		background: "gray.50",
		children: [mobileHeader, /* @__PURE__ */ jsxs(Box, {
			flex: "1",
			minHeight: "0",
			display: {
				base: "flex",
				md: "grid"
			},
			flexDirection: "column",
			gridTemplateAreas: `"logo topbar" "nav main"`,
			gridTemplateColumns: isOpen ? `${SIDEBAR_EXPANDED_WIDTH} 1fr` : `${SIDEBAR_COLLAPSED_WIDTH} 1fr`,
			gridTemplateRows: `${SIDEBAR_COLLAPSED_WIDTH} 1fr`,
			columnGap: "2",
			transition: "grid-template-columns 0.2s",
			background: "gray.50",
			children: [
				/* @__PURE__ */ jsx(Box, {
					display: {
						base: "none",
						md: "flex"
					},
					gridArea: "logo",
					height: HEADER_HEIGHT,
					alignItems: "center",
					justifyContent: "center",
					background: SIDEBAR_BACKGROUND_COLOR,
					borderTopLeftRadius: "xl",
					borderTopRightRadius: "xl",
					padding: "2.5",
					children: logo
				}),
				topbar,
				/* @__PURE__ */ jsx(Box, {
					as: "nav",
					display: {
						base: "none",
						md: "flex"
					},
					gridArea: "nav",
					flexDirection: "column",
					overflow: "hidden",
					children: nav
				}),
				/* @__PURE__ */ jsx(Box, {
					as: "main",
					gridArea: "main",
					display: "flex",
					flexDirection: "column",
					flex: "1",
					overflowY: "auto",
					minHeight: "0",
					background: "white",
					borderWidth: "1px",
					borderColor: "gray.200",
					borderTop: "none",
					borderBottomLeftRadius: "xl",
					borderBottomRightRadius: "xl",
					children
				})
			]
		})]
	});
}
// src/widgets/authenticated-layout/MobileHeader.tsx
function MobileHeader({ logo, nav, actions, title }) {
	return /* @__PURE__ */ jsxs(Flex, {
		display: {
			base: "flex",
			md: "none"
		},
		flexDirection: "column",
		background: "white",
		boxShadow: "sm",
		flexShrink: 0,
		children: [/* @__PURE__ */ jsxs(Flex, {
			alignItems: "center",
			justifyContent: "space-between",
			height: "56px",
			paddingX: "4",
			boxShadow: "sm",
			children: [/* @__PURE__ */ jsxs(Flex, {
				alignItems: "center",
				gap: "3",
				children: [nav, logo]
			}), /* @__PURE__ */ jsx(Flex, {
				alignItems: "center",
				gap: "1",
				children: actions
			})]
		}), title && /* @__PURE__ */ jsx(Box, {
			paddingX: "4",
			paddingY: "3",
			fontSize: "lg",
			fontWeight: "bold",
			color: "shell",
			boxShadow: "sm",
			zIndex: "1",
			children: title
		})]
	});
}
// src/widgets/authenticated-layout/TopBar.tsx
function TopBar({ title, actions }) {
	return /* @__PURE__ */ jsxs(Flex, {
		display: {
			base: "none",
			md: "flex"
		},
		gridArea: "topbar",
		alignItems: "center",
		justifyContent: "space-between",
		background: "white",
		height: HEADER_HEIGHT,
		paddingX: "4",
		borderWidth: "1px",
		borderColor: "gray.200",
		borderTopLeftRadius: "xl",
		borderTopRightRadius: "xl",
		children: [/* @__PURE__ */ jsx(Box, {
			as: "h1",
			margin: "0",
			fontSize: "xl",
			fontWeight: "bold",
			color: TITLE_COLOR,
			children: title
		}), /* @__PURE__ */ jsx(Flex, {
			alignItems: "center",
			gap: "1",
			children: actions
		})]
	});
}
// src/widgets/not-found/NotFound.tsx
function NotFound({ description }) {
	return /* @__PURE__ */ jsxs(Flex, {
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: "4",
		height: "full",
		padding: "8",
		textAlign: "center",
		children: [
			/* @__PURE__ */ jsx(GhostIcon, {
				width: "16",
				height: "16",
				color: "gray.400"
			}),
			/* @__PURE__ */ jsx(Text, {
				fontSize: "9xl",
				fontWeight: "extrabold",
				lineHeight: "1",
				color: "brand.solid",
				children: "404"
			}),
			/* @__PURE__ */ jsx(Text, {
				fontSize: "xl",
				color: "gray.500",
				children: description
			})
		]
	});
}
// src/widgets/page/PageContext.tsx
const PageContext = createContext(null);
function PageProvider({ children }) {
	const [title, setTitle] = useState("");
	return /* @__PURE__ */ jsx(PageContext.Provider, {
		value: {
			title,
			setTitle
		},
		children
	});
}
function usePageTitle() {
	const context = useContext(PageContext);
	if (!context) throw new Error("usePageTitle must be used within PageProvider");
	return context;
}
function useSetPageTitle({ title }) {
	const { setTitle } = usePageTitle();
	useEffect(() => {
		setTitle(title);
		return () => {
			setTitle("");
		};
	}, [title, setTitle]);
}
// src/widgets/page/Page.tsx
function PageRoot({ children }) {
	return /* @__PURE__ */ jsx(Flex, {
		flexDirection: "column",
		flex: "1",
		minHeight: "0",
		children
	});
}
function PageHeader({ title }) {
	useSetPageTitle({ title });
	return null;
}
function PageMain({ children, fillHeight }) {
	return /* @__PURE__ */ jsx(Flex, {
		flexDirection: "column",
		flex: "1",
		minHeight: fillHeight ? "0" : void 0,
		padding: "4",
		gap: "4",
		children
	});
}
const Page = {
	Root: PageRoot,
	Header: PageHeader,
	Main: PageMain
};
// src/widgets/sidebar/constants.ts
const BACKGROUND_COLOR = "shell";
const BACKGROUND_COLOR_HOVER = "rgba(255, 255, 255, 0.1)";
const BORDER_COLOR = "rgba(255, 255, 255, 0.15)";
const TEXT_COLOR = "rgba(255, 255, 255, 0.75)";
const TEXT_COLOR_ACTIVE = "white";
const SECTION_HEADING_COLOR = "rgba(255, 255, 255, 0.6)";
const TOOLTIP_BACKGROUND_COLOR = "var(--chakra-colors-brand-900)";
// src/widgets/sidebar/useSidebarStorage.ts
function useSidebarStorage(openKey, defaultOpen = false) {
	const [isOpen, setIsOpen] = useState(() => {
		const stored = localStorage.getItem(openKey);
		return stored === null ? defaultOpen : stored === "true";
	});
	return {
		isOpen,
		toggle: useCallback(() => {
			setIsOpen((prev) => {
				const next = !prev;
				localStorage.setItem(openKey, String(next));
				return next;
			});
		}, [openKey])
	};
}
// src/widgets/sidebar/SidebarContext.tsx
const SidebarContext = createContext(null);
function useSidebar() {
	const context = useContext(SidebarContext);
	if (!context) throw new Error("useSidebar must be used within SidebarProvider");
	return context;
}
function SidebarProvider({ children, openKey, defaultOpen = false }) {
	const { isOpen, toggle } = useSidebarStorage(openKey, defaultOpen);
	return /* @__PURE__ */ jsx(SidebarContext.Provider, {
		value: {
			isOpen,
			toggle
		},
		children
	});
}
// src/widgets/sidebar/desktop/DesktopFooter.tsx
function DesktopFooter({ expandLabel = "Agrandir", collapseLabel = "Réduire" }) {
	const { isOpen, toggle } = useSidebar();
	return /* @__PURE__ */ jsx(Box, {
		flexShrink: 0,
		padding: "4",
		background: BACKGROUND_COLOR,
		children: /* @__PURE__ */ jsx(Flex, {
			justifyContent: isOpen ? "flex-end" : "center",
			width: "full",
			children: /* @__PURE__ */ jsx(Tooltip, {
				content: expandLabel,
				disabled: isOpen,
				positioning: { placement: "right" },
				contentProps: { css: { "--tooltip-bg": TOOLTIP_BACKGROUND_COLOR } },
				openDelay: 200,
				closeDelay: 100,
				children: /* @__PURE__ */ jsx(Flex, {
					as: "button",
					gap: isOpen ? 2 : 0,
					alignItems: "center",
					width: isOpen ? "full" : "initial",
					padding: "2.5",
					borderRadius: "sm",
					cursor: "pointer",
					color: TEXT_COLOR,
					_hover: {
						bg: BACKGROUND_COLOR_HOVER,
						color: TEXT_COLOR_ACTIVE
					},
					onClick: toggle,
					children: isOpen ? /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(ArrowLeftToLineIcon, { size: "md" }), /* @__PURE__ */ jsx(Box, {
						as: "span",
						fontSize: "xs",
						children: collapseLabel
					})] }) : /* @__PURE__ */ jsx(ArrowRightToLineIcon, { size: "md" })
				})
			})
		})
	});
}
// src/widgets/sidebar/NavBadge.tsx
function NavBadge({ count }) {
	return /* @__PURE__ */ jsx(Flex, {
		as: "span",
		alignItems: "center",
		justifyContent: "center",
		width: "5",
		height: "5",
		borderRadius: "full",
		background: "brand.solid",
		flexShrink: 0,
		overflow: "hidden",
		children: /* @__PURE__ */ jsx(Box, {
			as: "span",
			fontSize: "2xs",
			fontWeight: "bold",
			color: "white",
			lineHeight: "1",
			children: count > 99 ? "99+" : count
		})
	});
}
const ITEM_STYLES$1 = {
	alignItems: "center",
	paddingY: "2",
	borderRadius: "sm",
	cursor: "pointer",
	_hover: {
		background: BACKGROUND_COLOR_HOVER,
		color: TEXT_COLOR_ACTIVE
	}
};
const LINK_CSS$1 = {
	"& a": {
		color: "inherit",
		textDecoration: "none",
		transition: "none"
	},
	"& a:hover, & a:focus": {
		color: "inherit",
		textDecoration: "none"
	}
};
// ../node_modules/@floating-ui/utils/dist/floating-ui.utils.mjs
const min = Math.min;
const max = Math.max;
const round = Math.round;
const floor = Math.floor;
const createCoords = (v) => ({
	x: v,
	y: v
});
const oppositeSideMap = {
	left: "right",
	right: "left",
	bottom: "top",
	top: "bottom"
};
function clamp(start, value, end) {
	return max(start, min(value, end));
}
function evaluate(value, param) {
	return typeof value === "function" ? value(param) : value;
}
function getSide(placement) {
	return placement.split("-")[0];
}
function getAlignment(placement) {
	return placement.split("-")[1];
}
function getOppositeAxis(axis) {
	return axis === "x" ? "y" : "x";
}
function getAxisLength(axis) {
	return axis === "y" ? "height" : "width";
}
function getSideAxis(placement) {
	const firstChar = placement[0];
	return firstChar === "t" || firstChar === "b" ? "y" : "x";
}
function getAlignmentAxis(placement) {
	return getOppositeAxis(getSideAxis(placement));
}
function getAlignmentSides(placement, rects, rtl) {
	if (rtl === void 0) rtl = false;
	const alignment = getAlignment(placement);
	const alignmentAxis = getAlignmentAxis(placement);
	const length = getAxisLength(alignmentAxis);
	let mainAlignmentSide = alignmentAxis === "x" ? alignment === (rtl ? "end" : "start") ? "right" : "left" : alignment === "start" ? "bottom" : "top";
	if (rects.reference[length] > rects.floating[length]) mainAlignmentSide = getOppositePlacement(mainAlignmentSide);
	return [mainAlignmentSide, getOppositePlacement(mainAlignmentSide)];
}
function getExpandedPlacements(placement) {
	const oppositePlacement = getOppositePlacement(placement);
	return [
		getOppositeAlignmentPlacement(placement),
		oppositePlacement,
		getOppositeAlignmentPlacement(oppositePlacement)
	];
}
function getOppositeAlignmentPlacement(placement) {
	return placement.includes("start") ? placement.replace("start", "end") : placement.replace("end", "start");
}
const lrPlacement = ["left", "right"];
const rlPlacement = ["right", "left"];
const tbPlacement = ["top", "bottom"];
const btPlacement = ["bottom", "top"];
function getSideList(side, isStart, rtl) {
	switch (side) {
		case "top":
		case "bottom":
			if (rtl) return isStart ? rlPlacement : lrPlacement;
			return isStart ? lrPlacement : rlPlacement;
		case "left":
		case "right": return isStart ? tbPlacement : btPlacement;
		default: return [];
	}
}
function getOppositeAxisPlacements(placement, flipAlignment, direction, rtl) {
	const alignment = getAlignment(placement);
	let list = getSideList(getSide(placement), direction === "start", rtl);
	if (alignment) {
		list = list.map((side) => side + "-" + alignment);
		if (flipAlignment) list = list.concat(list.map(getOppositeAlignmentPlacement));
	}
	return list;
}
function getOppositePlacement(placement) {
	const side = getSide(placement);
	return oppositeSideMap[side] + placement.slice(side.length);
}
function rectToClientRect(rect) {
	const { x, y, width, height } = rect;
	return {
		width,
		height,
		top: y,
		left: x,
		right: x + width,
		bottom: y + height,
		x,
		y
	};
}
// ../node_modules/@floating-ui/core/dist/floating-ui.core.mjs
/**
* Optimizes the visibility of the floating element by flipping the `placement`
* in order to keep it in view when the preferred placement(s) will overflow the
* clipping boundary. Alternative to `autoPlacement`.
* @see https://floating-ui.com/docs/flip
*/
const flip$1 = function(options) {
	if (options === void 0) options = {};
	return {
		name: "flip",
		options,
		async fn(state) {
			var _middlewareData$arrow, _middlewareData$flip;
			const { placement, middlewareData, rects, initialPlacement, platform, elements } = state;
			const { mainAxis: checkMainAxis = true, crossAxis: checkCrossAxis = true, fallbackPlacements: specifiedFallbackPlacements, fallbackStrategy = "bestFit", fallbackAxisSideDirection = "none", flipAlignment = true, ...detectOverflowOptions } = evaluate(options, state);
			if ((_middlewareData$arrow = middlewareData.arrow) != null && _middlewareData$arrow.alignmentOffset) return {};
			const side = getSide(placement);
			const initialSideAxis = getSideAxis(initialPlacement);
			const isBasePlacement = getSide(initialPlacement) === initialPlacement;
			const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
			const fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipAlignment ? [getOppositePlacement(initialPlacement)] : getExpandedPlacements(initialPlacement));
			const hasFallbackAxisSideDirection = fallbackAxisSideDirection !== "none";
			if (!specifiedFallbackPlacements && hasFallbackAxisSideDirection) fallbackPlacements.push(...getOppositeAxisPlacements(initialPlacement, flipAlignment, fallbackAxisSideDirection, rtl));
			const placements = [initialPlacement, ...fallbackPlacements];
			const overflow = await platform.detectOverflow(state, detectOverflowOptions);
			const overflows = [];
			let overflowsData = ((_middlewareData$flip = middlewareData.flip) == null ? void 0 : _middlewareData$flip.overflows) || [];
			if (checkMainAxis) overflows.push(overflow[side]);
			if (checkCrossAxis) {
				const sides = getAlignmentSides(placement, rects, rtl);
				overflows.push(overflow[sides[0]], overflow[sides[1]]);
			}
			overflowsData = [...overflowsData, {
				placement,
				overflows
			}];
			if (!overflows.every((side) => side <= 0)) {
				var _middlewareData$flip2, _overflowsData$filter;
				const nextIndex = (((_middlewareData$flip2 = middlewareData.flip) == null ? void 0 : _middlewareData$flip2.index) || 0) + 1;
				const nextPlacement = placements[nextIndex];
				if (nextPlacement) {
					if (!(checkCrossAxis === "alignment" ? initialSideAxis !== getSideAxis(nextPlacement) : false) || overflowsData.every((d) => getSideAxis(d.placement) === initialSideAxis ? d.overflows[0] > 0 : true)) return {
						data: {
							index: nextIndex,
							overflows: overflowsData
						},
						reset: { placement: nextPlacement }
					};
				}
				let resetPlacement = (_overflowsData$filter = overflowsData.filter((d) => d.overflows[0] <= 0).sort((a, b) => a.overflows[1] - b.overflows[1])[0]) == null ? void 0 : _overflowsData$filter.placement;
				if (!resetPlacement) switch (fallbackStrategy) {
					case "bestFit": {
						var _overflowsData$filter2;
						const placement = (_overflowsData$filter2 = overflowsData.filter((d) => {
							if (hasFallbackAxisSideDirection) {
								const currentSideAxis = getSideAxis(d.placement);
								return currentSideAxis === initialSideAxis || currentSideAxis === "y";
							}
							return true;
						}).map((d) => [d.placement, d.overflows.filter((overflow) => overflow > 0).reduce((acc, overflow) => acc + overflow, 0)]).sort((a, b) => a[1] - b[1])[0]) == null ? void 0 : _overflowsData$filter2[0];
						if (placement) resetPlacement = placement;
						break;
					}
					case "initialPlacement": resetPlacement = initialPlacement;
				}
				if (placement !== resetPlacement) return { reset: { placement: resetPlacement } };
			}
			return {};
		}
	};
};
const originSides = /*#__PURE__*/ new Set(["left", "top"]);
async function convertValueToCoords(state, options) {
	const { placement, platform, elements } = state;
	const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
	const side = getSide(placement);
	const alignment = getAlignment(placement);
	const isVertical = getSideAxis(placement) === "y";
	const mainAxisMulti = originSides.has(side) ? -1 : 1;
	const crossAxisMulti = rtl && isVertical ? -1 : 1;
	const rawValue = evaluate(options, state);
	let { mainAxis, crossAxis, alignmentAxis } = typeof rawValue === "number" ? {
		mainAxis: rawValue,
		crossAxis: 0,
		alignmentAxis: null
	} : {
		mainAxis: rawValue.mainAxis || 0,
		crossAxis: rawValue.crossAxis || 0,
		alignmentAxis: rawValue.alignmentAxis
	};
	if (alignment && typeof alignmentAxis === "number") crossAxis = alignment === "end" ? alignmentAxis * -1 : alignmentAxis;
	return isVertical ? {
		x: crossAxis * crossAxisMulti,
		y: mainAxis * mainAxisMulti
	} : {
		x: mainAxis * mainAxisMulti,
		y: crossAxis * crossAxisMulti
	};
}
/**
* Modifies the placement by translating the floating element along the
* specified axes.
* A number (shorthand for `mainAxis` or distance), or an axes configuration
* object may be passed.
* @see https://floating-ui.com/docs/offset
*/
const offset$1 = function(options) {
	if (options === void 0) options = 0;
	return {
		name: "offset",
		options,
		async fn(state) {
			var _middlewareData$offse, _middlewareData$arrow;
			const { x, y, placement, middlewareData } = state;
			const diffCoords = await convertValueToCoords(state, options);
			if (placement === ((_middlewareData$offse = middlewareData.offset) == null ? void 0 : _middlewareData$offse.placement) && (_middlewareData$arrow = middlewareData.arrow) != null && _middlewareData$arrow.alignmentOffset) return {};
			return {
				x: x + diffCoords.x,
				y: y + diffCoords.y,
				data: {
					...diffCoords,
					placement
				}
			};
		}
	};
};
/**
* Optimizes the visibility of the floating element by shifting it in order to
* keep it in view when it will overflow the clipping boundary.
* @see https://floating-ui.com/docs/shift
*/
const shift$1 = function(options) {
	if (options === void 0) options = {};
	return {
		name: "shift",
		options,
		async fn(state) {
			const { x, y, placement, platform } = state;
			const { mainAxis: checkMainAxis = true, crossAxis: checkCrossAxis = false, limiter = { fn: (_ref) => {
				let { x, y } = _ref;
				return {
					x,
					y
				};
			} }, ...detectOverflowOptions } = evaluate(options, state);
			const coords = {
				x,
				y
			};
			const overflow = await platform.detectOverflow(state, detectOverflowOptions);
			const crossAxis = getSideAxis(placement);
			const mainAxis = getOppositeAxis(crossAxis);
			let mainAxisCoord = coords[mainAxis];
			let crossAxisCoord = coords[crossAxis];
			const clampCoord = (axis, coord) => clamp(coord + overflow[axis === "y" ? "top" : "left"], coord, coord - overflow[axis === "y" ? "bottom" : "right"]);
			if (checkMainAxis) mainAxisCoord = clampCoord(mainAxis, mainAxisCoord);
			if (checkCrossAxis) crossAxisCoord = clampCoord(crossAxis, crossAxisCoord);
			const limitedCoords = limiter.fn({
				...state,
				[mainAxis]: mainAxisCoord,
				[crossAxis]: crossAxisCoord
			});
			return {
				...limitedCoords,
				data: {
					x: limitedCoords.x - x,
					y: limitedCoords.y - y,
					enabled: {
						[mainAxis]: checkMainAxis,
						[crossAxis]: checkCrossAxis
					}
				}
			};
		}
	};
};
// ../node_modules/@floating-ui/utils/dist/floating-ui.utils.dom.mjs
function hasWindow() {
	return typeof window !== "undefined";
}
function getNodeName(node) {
	if (isNode(node)) return (node.nodeName || "").toLowerCase();
	return "#document";
}
function getWindow(node) {
	var _node$ownerDocument;
	return (node == null || (_node$ownerDocument = node.ownerDocument) == null ? void 0 : _node$ownerDocument.defaultView) || window;
}
function getDocumentElement(node) {
	var _ref;
	return (_ref = (isNode(node) ? node.ownerDocument : node.document) || window.document) == null ? void 0 : _ref.documentElement;
}
function isNode(value) {
	if (!hasWindow()) return false;
	return value instanceof Node || value instanceof getWindow(value).Node;
}
function isElement(value) {
	if (!hasWindow()) return false;
	return value instanceof Element || value instanceof getWindow(value).Element;
}
function isHTMLElement(value) {
	if (!hasWindow()) return false;
	return value instanceof HTMLElement || value instanceof getWindow(value).HTMLElement;
}
function isShadowRoot(value) {
	if (!hasWindow() || typeof ShadowRoot === "undefined") return false;
	return value instanceof ShadowRoot || value instanceof getWindow(value).ShadowRoot;
}
function isOverflowElement(element) {
	const { overflow, overflowX, overflowY, display } = getComputedStyle$1(element);
	return /auto|scroll|overlay|hidden|clip/.test(overflow + overflowY + overflowX) && display !== "inline" && display !== "contents";
}
let isWebKitValue;
function isWebKit() {
	if (isWebKitValue == null) isWebKitValue = typeof CSS !== "undefined" && CSS.supports && CSS.supports("-webkit-backdrop-filter", "none");
	return isWebKitValue;
}
function isLastTraversableNode(node) {
	return /^(html|body|#document)$/.test(getNodeName(node));
}
function getComputedStyle$1(element) {
	return getWindow(element).getComputedStyle(element);
}
function getParentNode(node) {
	if (getNodeName(node) === "html") return node;
	const result = node.assignedSlot || node.parentNode || isShadowRoot(node) && node.host || getDocumentElement(node);
	return isShadowRoot(result) ? result.host : result;
}
function getNearestOverflowAncestor(node) {
	const parentNode = getParentNode(node);
	if (isLastTraversableNode(parentNode)) return (node.ownerDocument || node).body;
	if (isHTMLElement(parentNode) && isOverflowElement(parentNode)) return parentNode;
	return getNearestOverflowAncestor(parentNode);
}
function getOverflowAncestors(node, list, traverseIframes) {
	var _node$ownerDocument2;
	if (list === void 0) list = [];
	if (traverseIframes === void 0) traverseIframes = true;
	const scrollableAncestor = getNearestOverflowAncestor(node);
	const isBody = scrollableAncestor === ((_node$ownerDocument2 = node.ownerDocument) == null ? void 0 : _node$ownerDocument2.body);
	const win = getWindow(scrollableAncestor);
	if (isBody) {
		const frameElement = getFrameElement(win);
		return list.concat(win, win.visualViewport || [], isOverflowElement(scrollableAncestor) ? scrollableAncestor : [], frameElement && traverseIframes ? getOverflowAncestors(frameElement) : []);
	} else return list.concat(scrollableAncestor, getOverflowAncestors(scrollableAncestor, [], traverseIframes));
}
function getFrameElement(win) {
	return win.parent && Object.getPrototypeOf(win.parent) ? win.frameElement : null;
}
// ../node_modules/@floating-ui/dom/dist/floating-ui.dom.mjs
function getCssDimensions(element) {
	const css = getComputedStyle$1(element);
	let width = parseFloat(css.width) || 0;
	let height = parseFloat(css.height) || 0;
	const hasOffset = isHTMLElement(element);
	const offsetWidth = hasOffset ? element.offsetWidth : width;
	const offsetHeight = hasOffset ? element.offsetHeight : height;
	const shouldFallback = round(width) !== offsetWidth || round(height) !== offsetHeight;
	if (shouldFallback) {
		width = offsetWidth;
		height = offsetHeight;
	}
	return {
		width,
		height,
		$: shouldFallback
	};
}
function unwrapElement(element) {
	return !isElement(element) ? element.contextElement : element;
}
function getScale(element) {
	const domElement = unwrapElement(element);
	if (!isHTMLElement(domElement)) return createCoords(1);
	const rect = domElement.getBoundingClientRect();
	const { width, height, $ } = getCssDimensions(domElement);
	let x = ($ ? round(rect.width) : rect.width) / width;
	let y = ($ ? round(rect.height) : rect.height) / height;
	if (!x || !Number.isFinite(x)) x = 1;
	if (!y || !Number.isFinite(y)) y = 1;
	return {
		x,
		y
	};
}
const noOffsets = /*#__PURE__*/ createCoords(0);
function getVisualOffsets(element) {
	const win = getWindow(element);
	if (!isWebKit() || !win.visualViewport) return noOffsets;
	return {
		x: win.visualViewport.offsetLeft,
		y: win.visualViewport.offsetTop
	};
}
function shouldAddVisualOffsets(element, isFixed, floatingOffsetParent) {
	if (isFixed === void 0) isFixed = false;
	return !!floatingOffsetParent && isFixed && floatingOffsetParent === getWindow(element);
}
function getBoundingClientRect(element, includeScale, isFixedStrategy, offsetParent) {
	if (includeScale === void 0) includeScale = false;
	if (isFixedStrategy === void 0) isFixedStrategy = false;
	const clientRect = element.getBoundingClientRect();
	const domElement = unwrapElement(element);
	let scale = createCoords(1);
	if (includeScale) {
		if (offsetParent) {
			if (isElement(offsetParent)) scale = getScale(offsetParent);
		} else scale = getScale(element);
	}
	const visualOffsets = shouldAddVisualOffsets(domElement, isFixedStrategy, offsetParent) ? getVisualOffsets(domElement) : createCoords(0);
	let x = (clientRect.left + visualOffsets.x) / scale.x;
	let y = (clientRect.top + visualOffsets.y) / scale.y;
	let width = clientRect.width / scale.x;
	let height = clientRect.height / scale.y;
	if (domElement && offsetParent) {
		const win = getWindow(domElement);
		const offsetWin = isElement(offsetParent) ? getWindow(offsetParent) : offsetParent;
		let currentWin = win;
		let currentIFrame = getFrameElement(currentWin);
		while (currentIFrame && offsetWin !== currentWin) {
			const iframeScale = getScale(currentIFrame);
			const iframeRect = currentIFrame.getBoundingClientRect();
			const css = getComputedStyle$1(currentIFrame);
			const left = iframeRect.left + (currentIFrame.clientLeft + parseFloat(css.paddingLeft)) * iframeScale.x;
			const top = iframeRect.top + (currentIFrame.clientTop + parseFloat(css.paddingTop)) * iframeScale.y;
			x *= iframeScale.x;
			y *= iframeScale.y;
			width *= iframeScale.x;
			height *= iframeScale.y;
			x += left;
			y += top;
			currentWin = getWindow(currentIFrame);
			currentIFrame = getFrameElement(currentWin);
		}
	}
	return rectToClientRect({
		width,
		height,
		x,
		y
	});
}
function rectsAreEqual(a, b) {
	return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}
function observeMove(element, onMove, ancestorResize) {
	let io = null;
	let timeoutId;
	const root = getDocumentElement(element);
	function cleanup() {
		var _io;
		clearTimeout(timeoutId);
		(_io = io) == null || _io.disconnect();
		io = null;
	}
	function refresh(skip, threshold) {
		if (skip === void 0) skip = false;
		if (threshold === void 0) threshold = 1;
		cleanup();
		const elementRectForRootMargin = element.getBoundingClientRect();
		const { left, top, width, height } = elementRectForRootMargin;
		if (!skip) onMove();
		if (!width || !height) return;
		const insetTop = floor(top);
		const insetRight = floor(root.clientWidth - (left + width));
		const insetBottom = floor(root.clientHeight - (top + height));
		const insetLeft = floor(left);
		const options = {
			rootMargin: -insetTop + "px " + -insetRight + "px " + -insetBottom + "px " + -insetLeft + "px",
			threshold: max(0, min(1, threshold)) || 1
		};
		let isFirstUpdate = true;
		function handleObserve(entries) {
			const ratio = entries[0].intersectionRatio;
			if (!rectsAreEqual(elementRectForRootMargin, element.getBoundingClientRect())) return refresh();
			if (ratio !== threshold) {
				if (!isFirstUpdate) return refresh();
				if (!ratio) timeoutId = setTimeout(() => {
					refresh(false, 1e-7);
				}, 1e3);
				else refresh(false, ratio);
			}
			isFirstUpdate = false;
		}
		try {
			io = new IntersectionObserver(handleObserve, {
				...options,
				root: root.ownerDocument
			});
		} catch (_e) {
			io = new IntersectionObserver(handleObserve, options);
		}
		io.observe(element);
	}
	const win = getWindow(element);
	const handleResize = () => refresh(ancestorResize);
	win.addEventListener("resize", handleResize);
	refresh(true);
	return () => {
		win.removeEventListener("resize", handleResize);
		cleanup();
	};
}
/**
* Automatically updates the position of the floating element when necessary.
* Should only be called when the floating element is mounted on the DOM or
* visible on the screen.
* @returns cleanup function that should be invoked when the floating element is
* removed from the DOM or hidden from the screen.
* @see https://floating-ui.com/docs/autoUpdate
*/
function autoUpdate$1(reference, floating, update, options) {
	if (options === void 0) options = {};
	const { ancestorScroll = true, ancestorResize = true, elementResize = typeof ResizeObserver === "function", layoutShift = typeof IntersectionObserver === "function", animationFrame = false } = options;
	const referenceEl = unwrapElement(reference);
	const ancestors = ancestorScroll || ancestorResize ? [...referenceEl ? getOverflowAncestors(referenceEl) : [], ...floating ? getOverflowAncestors(floating) : []] : [];
	ancestors.forEach((ancestor) => {
		ancestorScroll && ancestor.addEventListener("scroll", update);
		ancestorResize && ancestor.addEventListener("resize", update);
	});
	const cleanupIo = referenceEl && layoutShift ? observeMove(referenceEl, update, ancestorResize) : null;
	let reobserveFrame = -1;
	let resizeObserver = null;
	if (elementResize) {
		resizeObserver = new ResizeObserver((_ref) => {
			let [firstEntry] = _ref;
			if (firstEntry && firstEntry.target === referenceEl && resizeObserver && floating) {
				resizeObserver.unobserve(floating);
				cancelAnimationFrame(reobserveFrame);
				reobserveFrame = requestAnimationFrame(() => {
					var _resizeObserver;
					(_resizeObserver = resizeObserver) == null || _resizeObserver.observe(floating);
				});
			}
			update();
		});
		if (referenceEl && !animationFrame) resizeObserver.observe(referenceEl);
		if (floating) resizeObserver.observe(floating);
	}
	let frameId;
	let prevRefRect = animationFrame ? getBoundingClientRect(reference) : null;
	if (animationFrame) frameLoop();
	function frameLoop() {
		const nextRefRect = getBoundingClientRect(reference);
		if (prevRefRect && !rectsAreEqual(prevRefRect, nextRefRect)) update();
		prevRefRect = nextRefRect;
		frameId = requestAnimationFrame(frameLoop);
	}
	update();
	return () => {
		var _resizeObserver2;
		ancestors.forEach((ancestor) => {
			ancestorScroll && ancestor.removeEventListener("scroll", update);
			ancestorResize && ancestor.removeEventListener("resize", update);
		});
		cleanupIo?.();
		(_resizeObserver2 = resizeObserver) == null || _resizeObserver2.disconnect();
		resizeObserver = null;
		if (animationFrame) cancelAnimationFrame(frameId);
	};
}
/**
* Modifies the placement by translating the floating element along the
* specified axes.
* A number (shorthand for `mainAxis` or distance), or an axes configuration
* object may be passed.
* @see https://floating-ui.com/docs/offset
*/
const offset$2 = offset$1;
/**
* Optimizes the visibility of the floating element by shifting it in order to
* keep it in view when it will overflow the clipping boundary.
* @see https://floating-ui.com/docs/shift
*/
const shift$2 = shift$1;
/**
* Optimizes the visibility of the floating element by flipping the `placement`
* in order to keep it in view when the preferred placement(s) will overflow the
* clipping boundary. Alternative to `autoPlacement`.
* @see https://floating-ui.com/docs/flip
*/
const flip$2 = flip$1;
// src/widgets/sidebar/desktop/useGroupFloating.ts
function useGroupFloating({ initialOpen = false } = {}) {
	const nodeId = useFloatingNodeId();
	const [isOpen, setIsOpen] = useState(initialOpen);
	const { refs, floatingStyles, context } = useFloating({
		nodeId,
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: "right-start",
		middleware: [
			offset$2(12),
			flip$2(),
			shift$2({ padding: 10 })
		],
		whileElementsMounted: autoUpdate$1
	});
	const hover = useHover(context, { handleClose: safePolygon({ buffer: 1 }) });
	const { getReferenceProps, getFloatingProps } = useInteractions([hover]);
	return {
		nodeId,
		isOpen,
		refs,
		floatingStyles,
		getReferenceProps,
		getFloatingProps
	};
}
// src/widgets/sidebar/desktop/DesktopGroup.tsx
function DesktopGroup({ navId, icon: Icon, label, active, badge, children }) {
	const { isOpen: isSidebarOpen } = useSidebar();
	const badgeValue = badge ?? 0;
	const { nodeId, isOpen: isDropdownOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } = useGroupFloating({ initialOpen: false });
	return /* @__PURE__ */ jsx(FloatingNode, {
		id: nodeId,
		children: /* @__PURE__ */ jsxs(Box, {
			as: "li",
			"data-tour": `nav.${navId}`,
			children: [/* @__PURE__ */ jsxs(Flex, {
				ref: refs.setReference,
				...ITEM_STYLES$1,
				...getReferenceProps(),
				width: "full",
				justifyContent: "flex-start",
				paddingX: isSidebarOpen ? "3" : "2",
				gap: isSidebarOpen ? 2 : 0,
				background: isDropdownOpen || active ? BACKGROUND_COLOR_HOVER : void 0,
				color: active ? TEXT_COLOR_ACTIVE : TEXT_COLOR,
				children: [/* @__PURE__ */ jsxs(Box, {
					as: "span",
					display: "flex",
					flexShrink: 0,
					padding: .5,
					position: "relative",
					children: [Icon && /* @__PURE__ */ jsx(Icon, {
						size: isSidebarOpen ? "sm" : "md",
						transition: "width 0.3s ease-in-out, height 0.3s ease-in-out"
					}), !isSidebarOpen && badgeValue > 0 && /* @__PURE__ */ jsx(Box, {
						position: "absolute",
						top: "-3",
						right: "-3",
						children: /* @__PURE__ */ jsx(NavBadge, { count: badgeValue })
					})]
				}), /* @__PURE__ */ jsxs(Flex, {
					as: "span",
					flex: "1",
					alignItems: "center",
					justifyContent: "space-between",
					overflow: "hidden",
					whiteSpace: "nowrap",
					opacity: isSidebarOpen ? 1 : 0,
					maxWidth: isSidebarOpen ? "500px" : "0",
					transition: "max-width 0.2s ease-in-out",
					children: [/* @__PURE__ */ jsx(Box, {
						as: "span",
						textAlign: "left",
						children: label
					}), /* @__PURE__ */ jsxs(Flex, {
						alignItems: "center",
						gap: 1,
						children: [badgeValue > 0 && /* @__PURE__ */ jsx(NavBadge, { count: badgeValue }), /* @__PURE__ */ jsx(ChevronRightIcon, { size: "sm" })]
					})]
				})]
			}), isDropdownOpen && /* @__PURE__ */ jsx(FloatingPortal, { children: /* @__PURE__ */ jsxs(Flex, {
				flexDirection: "column",
				gap: "2",
				ref: refs.setFloating,
				background: "shell",
				minWidth: "220px",
				padding: "2.5",
				boxShadow: "sm",
				borderRadius: "sm",
				zIndex: 9999,
				style: floatingStyles,
				"data-tour": `nav.${navId}.submenu`,
				...getFloatingProps(),
				children: [!isSidebarOpen && /* @__PURE__ */ jsx(Box, {
					fontSize: "xs",
					color: "rgba(255, 255, 255, 0.6)",
					textTransform: "uppercase",
					letterSpacing: "wider",
					paddingX: "2",
					children: label
				}), /* @__PURE__ */ jsx(Flex, {
					as: "ul",
					flexDirection: "column",
					gap: "1",
					margin: 0,
					color: "rgba(255, 255, 255, 0.75)",
					fontSize: "xs",
					listStyle: "none",
					children
				})]
			}) })]
		})
	});
}
// src/widgets/sidebar/desktop/DesktopLink.tsx
function DesktopLink({ navId, icon: Icon, render, children, badge }) {
	const { isOpen } = useSidebar();
	const badgeValue = badge ?? 0;
	const hasPositiveBadgeValue = badgeValue > 0;
	const buildContent = ({ isActive }) => /* @__PURE__ */ jsxs(Flex, {
		...ITEM_STYLES$1,
		justifyContent: "flex-start",
		background: isActive ? BACKGROUND_COLOR_HOVER : void 0,
		color: isActive ? TEXT_COLOR_ACTIVE : TEXT_COLOR,
		paddingX: isOpen ? "3" : "2",
		gap: isOpen ? 2 : 0,
		children: [Icon && /* @__PURE__ */ jsxs(Box, {
			as: "span",
			display: "flex",
			flexShrink: 0,
			padding: .5,
			position: "relative",
			children: [/* @__PURE__ */ jsx(Icon, {
				size: isOpen ? "sm" : "md",
				transition: "width 0.3s ease-in-out, height 0.3s ease-in-out"
			}), !isOpen && hasPositiveBadgeValue && /* @__PURE__ */ jsx(Box, {
				position: "absolute",
				top: "-3",
				right: "-3",
				children: /* @__PURE__ */ jsx(NavBadge, { count: badgeValue })
			})]
		}), /* @__PURE__ */ jsxs(Flex, {
			as: "span",
			flex: "1",
			alignItems: "center",
			justifyContent: "space-between",
			gap: 2,
			overflow: "hidden",
			whiteSpace: "nowrap",
			opacity: isOpen ? 1 : 0,
			maxWidth: isOpen ? "200px" : "0",
			transition: "max-width 0.2s ease-in-out",
			children: [/* @__PURE__ */ jsx(Box, {
				as: "span",
				children
			}), hasPositiveBadgeValue && /* @__PURE__ */ jsx(NavBadge, { count: badgeValue })]
		})]
	});
	return /* @__PURE__ */ jsx(Tooltip, {
		content: children,
		contentProps: { css: { "--tooltip-bg": TOOLTIP_BACKGROUND_COLOR } },
		disabled: isOpen,
		openDelay: 200,
		closeDelay: 100,
		positioning: { placement: "right" },
		children: /* @__PURE__ */ jsx(Box, {
			as: "li",
			css: LINK_CSS$1,
			"data-tour": `nav.${navId}`,
			children: render(buildContent)
		})
	});
}
// src/widgets/sidebar/desktop/DesktopMenu.tsx
function DesktopMenu({ children }) {
	return /* @__PURE__ */ jsx(Flex, {
		flexDirection: "column",
		flex: "1",
		overflowY: "auto",
		minHeight: "0",
		css: {
			scrollbarWidth: "none",
			"&::-webkit-scrollbar": { display: "none" }
		},
		children: /* @__PURE__ */ jsx(FloatingTree, { children: /* @__PURE__ */ jsx(Flex, {
			as: "ul",
			flexDirection: "column",
			gap: "1",
			listStyle: "none",
			margin: 0,
			padding: "2.5",
			background: BACKGROUND_COLOR,
			fontSize: "xs",
			children
		}) })
	});
}
// src/widgets/sidebar/desktop/DesktopRoot.tsx
function DesktopRoot({ children }) {
	return /* @__PURE__ */ jsx(Flex, {
		flexDirection: "column",
		background: BACKGROUND_COLOR,
		overflow: "hidden",
		height: "full",
		borderBottomLeftRadius: "xl",
		borderBottomRightRadius: "xl",
		children
	});
}
// src/widgets/sidebar/desktop/DesktopSectionHeading.tsx
function DesktopSectionHeading({ label, children }) {
	const { isOpen } = useSidebar();
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [isOpen ? /* @__PURE__ */ jsx(Box, {
		as: "li",
		listStyle: "none",
		paddingTop: "2",
		paddingBottom: "1",
		fontSize: "2xs",
		color: SECTION_HEADING_COLOR,
		textTransform: "uppercase",
		letterSpacing: "wider",
		whiteSpace: "nowrap",
		overflow: "hidden",
		children: label
	}) : /* @__PURE__ */ jsx(Box, {
		as: "li",
		listStyle: "none",
		marginY: "2",
		marginX: "2",
		borderTop: "1px solid",
		borderColor: BORDER_COLOR
	}), children] });
}
// src/widgets/sidebar/desktop/DesktopSkeleton.tsx
function DesktopSkeleton() {
	const { isOpen } = useSidebar();
	return /* @__PURE__ */ jsx(Fragment$1, { children: Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsx(Box, {
		as: "li",
		listStyle: "none",
		children: /* @__PURE__ */ jsxs(Flex, {
			alignItems: "center",
			justifyContent: isOpen ? "flex-start" : "center",
			gap: "2",
			paddingX: "3",
			paddingY: "2",
			borderRadius: "sm",
			children: [/* @__PURE__ */ jsx(Skeleton, {
				height: "6",
				width: "6",
				borderRadius: "sm",
				flexShrink: 0
			}), isOpen && /* @__PURE__ */ jsx(Skeleton, {
				height: "4",
				flex: "1",
				borderRadius: "sm"
			})]
		})
	}, i)) });
}
// src/widgets/sidebar/desktop/DesktopSubGroup.tsx
function DesktopSubGroup({ navId, label, active, children }) {
	const { nodeId, isOpen: isDropdownOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } = useGroupFloating();
	return /* @__PURE__ */ jsx(FloatingNode, {
		id: nodeId,
		children: /* @__PURE__ */ jsxs(Box, {
			as: "li",
			"data-tour": `nav.${navId}`,
			children: [/* @__PURE__ */ jsx(Flex, {
				ref: refs.setReference,
				...ITEM_STYLES$1,
				...getReferenceProps(),
				width: "full",
				justifyContent: "flex-start",
				paddingX: "3",
				background: isDropdownOpen || active ? BACKGROUND_COLOR_HOVER : void 0,
				color: active ? TEXT_COLOR_ACTIVE : TEXT_COLOR,
				children: /* @__PURE__ */ jsxs(Flex, {
					as: "span",
					flex: "1",
					alignItems: "center",
					justifyContent: "space-between",
					children: [/* @__PURE__ */ jsx(Box, {
						as: "span",
						children: label
					}), /* @__PURE__ */ jsx(ChevronRightIcon, { size: "sm" })]
				})
			}), isDropdownOpen && /* @__PURE__ */ jsx(FloatingPortal, { children: /* @__PURE__ */ jsx(Flex, {
				as: "ul",
				flexDirection: "column",
				gap: "1",
				ref: refs.setFloating,
				background: "shell",
				minWidth: "220px",
				padding: "2.5",
				boxShadow: "sm",
				borderRadius: "sm",
				zIndex: 9999,
				style: floatingStyles,
				margin: 0,
				color: "rgba(255, 255, 255, 0.75)",
				fontSize: "xs",
				listStyle: "none",
				"data-tour": `nav.${navId}.submenu`,
				...getFloatingProps(),
				children
			}) })]
		})
	});
}
// src/widgets/sidebar/desktop/DesktopSubLink.tsx
function DesktopSubLink({ navId, render, children, badge }) {
	const badgeValue = badge ?? 0;
	const buildContent = ({ isActive }) => /* @__PURE__ */ jsxs(Flex, {
		...ITEM_STYLES$1,
		paddingX: "3",
		justifyContent: "space-between",
		background: isActive ? BACKGROUND_COLOR_HOVER : void 0,
		color: isActive ? TEXT_COLOR_ACTIVE : TEXT_COLOR,
		children: [/* @__PURE__ */ jsx(Box, {
			as: "span",
			children
		}), badgeValue > 0 && /* @__PURE__ */ jsx(NavBadge, { count: badgeValue })]
	});
	return /* @__PURE__ */ jsx(Box, {
		as: "li",
		css: LINK_CSS$1,
		"data-tour": `nav.${navId}`,
		children: render(buildContent)
	});
}
// src/widgets/sidebar/desktop/index.ts
const SidebarDesktop = {
	Root: DesktopRoot,
	Menu: DesktopMenu,
	Footer: DesktopFooter,
	Link: DesktopLink,
	SubLink: DesktopSubLink,
	Group: DesktopGroup,
	SubGroup: DesktopSubGroup,
	SectionHeading: DesktopSectionHeading,
	Skeleton: DesktopSkeleton
};
// src/widgets/sidebar/renderNavNodes.tsx
function isNodeActive(node, pathname) {
	if (node.kind === "link") return pathname === node.to || pathname.startsWith(node.to + "/");
	return node.children.some((child) => isNodeActive(child, pathname));
}
function renderTopLevelNode(node, pathname, renderLink) {
	if (node.kind === "link") return /* @__PURE__ */ jsx(SidebarDesktop.Link, {
		navId: node.id,
		icon: node.icon,
		render: renderLink(node.to),
		badge: node.badge,
		children: node.label
	}, node.id);
	return /* @__PURE__ */ jsx(SidebarDesktop.Group, {
		navId: node.id,
		icon: node.icon,
		label: node.label,
		active: isNodeActive(node, pathname),
		badge: node.badge,
		children: renderSubLevelNodes({
			nodes: node.children,
			pathname,
			renderLink
		})
	}, node.id);
}
function renderNavNodes({ nodes, pathname, renderLink }) {
	return nodes.map((node) => {
		if (node.kind === "section") return /* @__PURE__ */ jsx(SidebarDesktop.SectionHeading, {
			label: node.label,
			children: node.children.map((child) => renderTopLevelNode(child, pathname, renderLink))
		}, node.id);
		return renderTopLevelNode(node, pathname, renderLink);
	});
}
function renderSubLevelNodes({ nodes, pathname, renderLink }) {
	return nodes.map((node) => {
		if (node.kind === "link") return /* @__PURE__ */ jsx(SidebarDesktop.SubLink, {
			navId: node.id,
			render: renderLink(node.to),
			badge: node.badge,
			children: node.label
		}, node.id);
		return /* @__PURE__ */ jsx(SidebarDesktop.SubGroup, {
			navId: node.id,
			label: node.label,
			active: isNodeActive(node, pathname),
			children: renderSubLevelNodes({
				nodes: node.children,
				pathname,
				renderLink
			})
		}, node.id);
	});
}
// src/widgets/sidebar/mobile/constants.ts
const ITEM_STYLES = {
	display: "flex",
	alignItems: "center",
	gap: "2",
	paddingX: "3",
	paddingY: "2",
	borderRadius: "sm",
	cursor: "pointer",
	fontSize: "xs",
	width: "full",
	_hover: {
		background: BACKGROUND_COLOR_HOVER,
		color: TEXT_COLOR_ACTIVE
	}
};
const LINK_CSS = {
	"& a": {
		color: "inherit",
		textDecoration: "none",
		transition: "none"
	},
	"& a:hover, & a:focus": {
		color: "inherit",
		textDecoration: "none"
	}
};
// src/widgets/sidebar/mobile/renderMobileNodes.tsx
function renderMobileTopLevelNode(node, { pathname, renderLink, onClose }) {
	const badgeValue = node.badge ?? 0;
	const hasPositiveBadgeValue = badgeValue > 0;
	const Icon = node.icon;
	if (node.kind === "link") {
		const content = ({ isActive: active }) => /* @__PURE__ */ jsxs(Flex, {
			...ITEM_STYLES,
			background: active ? BACKGROUND_COLOR_HOVER : void 0,
			color: active ? TEXT_COLOR_ACTIVE : TEXT_COLOR,
			onClick: onClose,
			children: [
				/* @__PURE__ */ jsx(Box, {
					as: "span",
					flexShrink: 0,
					children: Icon && /* @__PURE__ */ jsx(Icon, { size: "sm" })
				}),
				/* @__PURE__ */ jsx(Box, {
					as: "span",
					flex: "1",
					children: node.label
				}),
				hasPositiveBadgeValue && /* @__PURE__ */ jsx(NavBadge, { count: badgeValue })
			]
		});
		return /* @__PURE__ */ jsx(Box, {
			css: LINK_CSS,
			children: renderLink(node.to)(content)
		}, node.id);
	}
	const active = isNodeActive(node, pathname);
	return /* @__PURE__ */ jsxs(Accordion.Item, {
		value: node.id,
		borderBottom: "none",
		children: [/* @__PURE__ */ jsxs(Accordion.ItemTrigger, {
			...ITEM_STYLES,
			color: active ? TEXT_COLOR_ACTIVE : TEXT_COLOR,
			background: active ? BACKGROUND_COLOR_HOVER : void 0,
			children: [
				/* @__PURE__ */ jsx(Box, {
					as: "span",
					flexShrink: 0,
					children: Icon && /* @__PURE__ */ jsx(Icon, { size: "sm" })
				}),
				/* @__PURE__ */ jsx(Box, {
					as: "span",
					flex: "1",
					textAlign: "left",
					children: node.label
				}),
				hasPositiveBadgeValue && /* @__PURE__ */ jsx(NavBadge, { count: badgeValue }),
				/* @__PURE__ */ jsx(Accordion.ItemIndicator, {})
			]
		}), /* @__PURE__ */ jsx(Accordion.ItemContent, { children: /* @__PURE__ */ jsx(Flex, {
			flexDirection: "column",
			gap: "1",
			paddingLeft: "4",
			paddingY: "1",
			children: renderMobileSubLevelNodes({
				nodes: node.children,
				pathname,
				renderLink,
				onClose
			})
		}) })]
	}, node.id);
}
function renderMobileTopLevelNodes({ nodes, pathname, renderLink, onClose }) {
	return nodes.map((node) => {
		if (node.kind === "section") return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(Box, {
			as: "li",
			listStyle: "none",
			paddingTop: "2",
			paddingBottom: "1",
			fontSize: "2xs",
			color: SECTION_HEADING_COLOR,
			textTransform: "uppercase",
			letterSpacing: "wider",
			children: node.label
		}), node.children.map((child) => renderMobileTopLevelNode(child, {
			pathname,
			renderLink,
			onClose
		}))] }, node.id);
		return renderMobileTopLevelNode(node, {
			pathname,
			renderLink,
			onClose
		});
	});
}
function renderMobileSubLevelNodes({ nodes, pathname, renderLink, onClose }) {
	return nodes.map((node) => {
		const badgeValue = node.badge ?? 0;
		const hasPositiveBadgeValue = badgeValue > 0;
		if (node.kind === "link") {
			const content = ({ isActive: active }) => /* @__PURE__ */ jsxs(Flex, {
				...ITEM_STYLES,
				background: active ? BACKGROUND_COLOR_HOVER : void 0,
				color: active ? TEXT_COLOR_ACTIVE : TEXT_COLOR,
				onClick: onClose,
				children: [/* @__PURE__ */ jsx(Box, {
					as: "span",
					flex: "1",
					children: node.label
				}), hasPositiveBadgeValue && /* @__PURE__ */ jsx(NavBadge, { count: badgeValue })]
			});
			return /* @__PURE__ */ jsx(Box, {
				css: LINK_CSS,
				children: renderLink(node.to)(content)
			}, node.id);
		}
		const active = isNodeActive(node, pathname);
		return /* @__PURE__ */ jsxs(Accordion.Item, {
			value: node.id,
			borderBottom: "none",
			children: [/* @__PURE__ */ jsxs(Accordion.ItemTrigger, {
				...ITEM_STYLES,
				color: active ? TEXT_COLOR_ACTIVE : TEXT_COLOR,
				background: active ? BACKGROUND_COLOR_HOVER : void 0,
				children: [
					/* @__PURE__ */ jsx(Box, {
						as: "span",
						flex: "1",
						textAlign: "left",
						children: node.label
					}),
					hasPositiveBadgeValue && /* @__PURE__ */ jsx(NavBadge, { count: badgeValue }),
					/* @__PURE__ */ jsx(Accordion.ItemIndicator, {})
				]
			}), /* @__PURE__ */ jsx(Accordion.ItemContent, { children: /* @__PURE__ */ jsx(Flex, {
				flexDirection: "column",
				gap: "1",
				paddingLeft: "4",
				paddingY: "1",
				children: renderMobileSubLevelNodes({
					nodes: node.children,
					pathname,
					renderLink,
					onClose
				})
			}) })]
		}, node.id);
	});
}
// src/widgets/sidebar/mobile/SidebarMobile.tsx
function SidebarMobile({ nodes, pathname, renderLink, logo }) {
	const [isOpen, setIsOpen] = useState(false);
	const onOpenChange = ({ open }) => {
		setIsOpen(open);
	};
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Flex, {
		as: "button",
		alignItems: "center",
		justifyContent: "center",
		padding: "2",
		borderRadius: "sm",
		cursor: "pointer",
		color: "gray.600",
		_hover: { background: "gray.100" },
		onClick: () => onOpenChange({ open: true }),
		"aria-label": "Ouvrir le menu",
		children: /* @__PURE__ */ jsx(MenuIcon, {})
	}), /* @__PURE__ */ jsxs(Drawer.Root, {
		open: isOpen,
		onOpenChange,
		placement: "start",
		children: [/* @__PURE__ */ jsx(Drawer.Backdrop, {}), /* @__PURE__ */ jsx(Drawer.Positioner, { children: /* @__PURE__ */ jsxs(Drawer.Content, {
			background: BACKGROUND_COLOR,
			maxWidth: "280px",
			children: [/* @__PURE__ */ jsx(Drawer.Header, {
				background: "white",
				paddingY: "0",
				children: /* @__PURE__ */ jsxs(Flex, {
					alignItems: "center",
					justifyContent: "space-between",
					height: "56px",
					children: [/* @__PURE__ */ jsx(Box, { children: logo }), /* @__PURE__ */ jsx(Drawer.CloseTrigger, {
						asChild: true,
						children: /* @__PURE__ */ jsx(IconButton, {
							variant: "ghost",
							color: TEXT_COLOR,
							children: /* @__PURE__ */ jsx(CircleXIcon, {})
						})
					})]
				})
			}), /* @__PURE__ */ jsx(Drawer.Body, {
				padding: "4",
				overflowY: "auto",
				css: {
					scrollbarWidth: "none",
					"&::-webkit-scrollbar": { display: "none" }
				},
				children: /* @__PURE__ */ jsx(Accordion.Root, {
					multiple: true,
					collapsible: true,
					children: /* @__PURE__ */ jsx(Flex, {
						as: "ul",
						flexDirection: "column",
						gap: "1",
						listStyle: "none",
						margin: 0,
						padding: 0,
						children: renderMobileTopLevelNodes({
							nodes,
							pathname,
							renderLink,
							onClose: () => onOpenChange({ open: false })
						})
					})
				})
			})]
		}) })]
	})] });
}
// src/widgets/unauthenticated-layout/UnauthenticatedLayout.tsx
var import_login_background = /* @__PURE__ */ __toESM((/* @__PURE__ */ __commonJSMin(((exports, module) => {
	module.exports = "../assets/login-background-rILrCYt4.png";
})))(), 1);
const BACKGROUND_PANEL = "shell";
function UnauthenticatedLayout({ children, panel, logo, footer, backButton }) {
	return /* @__PURE__ */ jsxs(Flex, {
		flexDirection: {
			base: "column",
			md: "row"
		},
		justifyContent: "center",
		gap: "4",
		padding: "2",
		height: "100vh",
		background: "white",
		children: [/* @__PURE__ */ jsx(Flex, {
			display: {
				base: "none",
				lg: "flex"
			},
			flexDirection: "column",
			flex: 1,
			height: "full",
			borderRadius: "xl",
			background: BACKGROUND_PANEL,
			backgroundImage: `url("${import_login_background.default}")`,
			backgroundSize: "cover",
			backgroundRepeat: "no-repeat",
			backgroundPosition: "center",
			children: /* @__PURE__ */ jsx(Flex, {
				alignItems: "center",
				justifyContent: "center",
				flex: 1,
				height: "full",
				padding: "4",
				children: panel
			})
		}), /* @__PURE__ */ jsxs(Flex, {
			flexDirection: "column",
			alignItems: "center",
			flex: 1,
			position: "relative",
			height: "full",
			gap: 4,
			paddingX: "4",
			paddingTop: "4",
			children: [
				backButton && /* @__PURE__ */ jsx(Flex, {
					position: "absolute",
					top: "4",
					left: "4",
					width: "full",
					alignSelf: "flex-start",
					zIndex: 1,
					children: backButton
				}),
				/* @__PURE__ */ jsxs(Flex, {
					flexDirection: "column",
					alignItems: "center",
					flex: 1,
					width: "full",
					overflow: "hidden",
					gap: 8,
					children: [/* @__PURE__ */ jsx(Flex, {
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "flex-end",
						flex: 1,
						children: logo
					}), /* @__PURE__ */ jsx(Flex, {
						flexDirection: "column",
						alignItems: "center",
						flex: 2,
						overflow: "hidden",
						width: "full",
						children
					})]
				}),
				/* @__PURE__ */ jsx(Flex, {
					as: "footer",
					justifyContent: "space-between",
					alignItems: "center",
					fontSize: "xs",
					width: "full",
					children: footer
				})
			]
		})]
	});
}
export { AppGrid, MobileHeader, NotFound, Page, PageProvider, SidebarDesktop, SidebarMobile, SidebarProvider, TopBar, UnauthenticatedLayout, isNodeActive, renderNavNodes, usePageTitle, useSetPageTitle, useSidebar };

//# sourceMappingURL=index.js.map